import { Modal } from 'antd'
import {
  ChatbotDataContext,
  Source,
} from 'features/chatbot/contexts/ChatbotDataLayer'
import { useGroupedConversation } from 'features/chatbot/hooks/useGroupedConversation'
import React, { useContext, useState } from 'react'
import styled from 'styled-components'

import { ConversationMessageComponent } from './ConversationMessage'
import { ThoughtsAccordion } from './ThoughtsAccordion'

const ConversationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0 0.5rem;
`

const MetadataModal = styled(Modal)`
  .ant-modal-content {
    background: ${({ theme }) => theme.forecast.cardBackground};
    border: 1px solid ${({ theme }) => theme.forecast.tertiaryText};
  }

  .ant-modal-header {
    border-bottom: 1px solid ${({ theme }) => theme.forecast.tertiaryText};
    padding-bottom: 0.25rem;
  }

  .ant-modal-title {
    color: ${({ theme }) => theme.forecast.defaultText};
    padding-right: 1.5rem;
    word-wrap: break-word;
  }

  .ant-modal-close {
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const MetadataContent = styled.pre`
  background: ${({ theme }) => theme.forecast.cardBackground};
  border: 1px solid ${({ theme }) => theme.forecast.tertiaryText};
  border-radius: 0.5rem;
  padding: 1rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.875rem;
  overflow-x: auto;
  max-height: 60vh;
`

export const Conversation: React.FC = () => {
  const { conversation, isDelphiThinking } = useContext(ChatbotDataContext)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedSource, setSelectedSource] = useState<Source | null>(null)

  const { conversationWithGroupedThoughts, activeThoughts } =
    useGroupedConversation(conversation)

  const handleSourceClick = (source: Source) => {
    setSelectedSource(source)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedSource(null)
  }

  return (
    <>
      <ConversationContainer>
        {conversationWithGroupedThoughts.map((item) => {
          if (item.type === 'thoughtGroup') {
            const thoughts = item.data
            return (
              <ThoughtsAccordion
                key={`thoughts-${item.index}`}
                accordionKey={`thoughts-${item.index}`}
                title={`Cantilever AI's Thoughts (${thoughts.length})`}
                thoughts={thoughts}
              />
            )
          }

          const message = item.data
          return (
            <ConversationMessageComponent
              key={item.index}
              message={message}
              itemIndex={item.index}
              onSourceClick={handleSourceClick}
            />
          )
        })}
        {isDelphiThinking && (
          <ThoughtsAccordion
            accordionKey='delphi-thinking'
            title='Cantilever AI is thinking...'
            thoughts={
              activeThoughts?.thoughts ?? [
                { text: 'Processing your request...' },
              ]
            }
          />
        )}
      </ConversationContainer>

      <MetadataModal
        title={selectedSource?.text || 'Source Details'}
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={600}>
        {selectedSource && (
          <MetadataContent>
            {JSON.stringify(selectedSource.data, null, 2)}
          </MetadataContent>
        )}
      </MetadataModal>
    </>
  )
}
